import { debugLog } from '../util/debug';
import {
    GET_SERVER,
    SET_SERVER_VERSION,
    SET_AUTOSCAN_TIMEOUT,
    SET_SYSTEM_OVERLAY,
    SET_PTZ_CONFIG,
    SET_PTZ_PRESETS,
    SET_ALL_CAMERAS,
    SET_AUTH_SERVERS,
    LOGOUT_USER,
    EXPIRE_SESSION,
    SET_EVIEW_MODE,
    SET_CONTROLLER_MODE,
    SET_CONTROLLER_INFO,
    SET_ALL_SERVERS,
    SET_ACTIVE_SERVER,
    SET_ENTERPRISE_CAMERAS,
    SET_ALL_EVIEW_CAMERAS,
    SET_SERVER_SESSIONS,
    ASSIGN_CAMERA_TO_SLOT,
    JUMP_SYSTEM,
    JUMP_SYSTEM_START,
    SET_MACHINE_STATUS,
    SET_USERS_ONLINE,
    SET_SYSTEM_NAME
  } from '../actions/types';

  // Build fixed-size page ranges ("1-2","3-4"… for conf-2, "1-8","9-16"… for conf-8)
  // for the camera-bank strip, mirroring the quadView/sixView convention: the first
  // page is always present + enabled; later pages appear once the camera count reaches
  // them and are enabled only if at least one slot in the range is enabled. Returns
  // { view: [[label, enabled], …], enabledArr: [enabledLabel, …] }. (#86bb5dx62 L-A1)
  const buildPagedRanges = (pageSize, numCams, enabledSlots) => {
    const view = [];
    const enabledArr = [];
    const lastSlot = Math.max(numCams, pageSize);
    for (let start = 1; start <= lastSlot; start += pageSize) {
      const label = `${start}-${start + pageSize - 1}`;
      let anyEnabled = false;
      for (let s = start; s < start + pageSize; s++) {
        if (enabledSlots.indexOf(s) > -1) { anyEnabled = true; break; }
      }
      const isEnabled = start === 1 ? true : anyEnabled;
      view.push([label, isEnabled]);
      if (isEnabled) enabledArr.push(label);
    }
    return { view, enabledArr };
  };

  // Helper function to build camera arrays for CamButtonsView
  const buildCameraArrays = (cameras) => {
    let cameraArr = [];
    let singleViewEnabledArr = [];
    let quadView = [ ["1-4", true] ];
    let quadViewEnabledArr = [ "1-4" ];
    let sixView = [ ["1-6", true] ];
    let sixViewEnabledArr = [ "1-6" ];
    let nineView = [ ["1-9", true] ];
    let nineViewEnabledArr = [ "1-9" ];
    let twelveView = [ ["1-12", true] ];
    let twelveViewEnabledArr = [ "1-12" ];
    let sixteenView = [ ["1-16", true] ];
    let sixteenViewEnabledArr = [ "1-16" ];

    // Build cameraArr based on slot positions, not camera IDs
    // This allows the same camera to appear in multiple slots
    for( let i = 0; i < cameras.length; i++) {
        const cam = cameras[i];
        const slotNumber = i + 1; // Slot numbers are 1-indexed for display
        const camEnabled = cam && cam.fEnable === true;
        if(camEnabled){
           singleViewEnabledArr.push(slotNumber);
        };
        // Format: [view_string, display_number, enabled]
        cameraArr.push([`cam_${slotNumber}`, slotNumber, camEnabled]);
    };

    // Build multi-camera view arrays
    if(cameras.length > 4) {
      if( singleViewEnabledArr.indexOf(5) < 0 && singleViewEnabledArr.indexOf(6) < 0 &&
          singleViewEnabledArr.indexOf(7) < 0 && singleViewEnabledArr.indexOf(8) < 0) {
        quadView.push( ["5-8", false] );
      } else {
        quadView.push( ["5-8", true] );
        quadViewEnabledArr.push( "5-8" );
      };
    };

    if(cameras.length > 8) {
      if( singleViewEnabledArr.indexOf(9) < 0 && singleViewEnabledArr.indexOf(10) < 0 &&
          singleViewEnabledArr.indexOf(11) < 0 && singleViewEnabledArr.indexOf(12) < 0) {
        quadView.push( ["9-12", false] );
      } else {
        quadView.push( ["9-12", true] );
        quadViewEnabledArr.push( "9-12" );
      };
    };

    // conf-2 (pages of 2) + conf-8 (pages of 8) bank ranges — #86bb5dx62 L-A1
    const { view: twoView, enabledArr: twoViewEnabledArr } = buildPagedRanges(2, cameras.length, singleViewEnabledArr);
    const { view: eightView, enabledArr: eightViewEnabledArr } = buildPagedRanges(8, cameras.length, singleViewEnabledArr);

    return {
      cameraArr,
      singleViewEnabledArr,
      twoView,
      twoViewEnabledArr,
      quadView,
      quadViewEnabledArr,
      sixView,
      sixViewEnabledArr,
      eightView,
      eightViewEnabledArr,
      nineView,
      nineViewEnabledArr,
      twelveView,
      twelveViewEnabledArr,
      sixteenView,
      sixteenViewEnabledArr
    };
  };

  const INITIAL_STATE = {
    bSerial: 0,
    bNumCams: 0,
    sIP: '',
    bPort: 0,
    sLocalIP: '',
    fLocal: false,
    sName: '',
    sVersion: '',
    sVersionMajor: '',
    sReleaseVersion: '',
    fReleaseCandidate: false,
    bTimestamp: 0,
    bTimeDiffmax: 36000,
    bAutoScanTimeout: 5,
    // NVR burn-in overlay (config.system) — captured at login by systemGetAll so the Setup overlay can
    // read it without a fragile on-open getAll (which hits the dev-proxy Connection:close artifact).
    // The *Supported flags = whether the key was present in getAll (older backends omit newer keys, e.g.
    // fOverlayBackground), so the Setup overlay can hide a toggle the NVR can't honor.
    fLiveOverlay: false,
    fOverlayBackground: false,
    fLiveOverlaySupported: false,
    fOverlayBackgroundSupported: false,
    ptzConfig: [],
    ptzPresets: [],
    cameras: [],
    cameraArr: [],
    twoView: [],
    twoViewEnabledArr: [],
    quadView: [],
    quadViewEnabledArr: [],
    sixView: [],
    sixViewEnabledArr: [],
    eightView: [],
    eightViewEnabledArr: [],
    nineView: [],
    nineViewEnabledArr: [],
    twelveView: [],
    twelveViewEnabledArr: [],
    sixteenView: [],
    sixteenViewEnabledArr: [],
    singleViewEnabledArray: [],
    authServers: [],
    sServer: '',
    sServerLiveMPE: '/mpe/',
    sServerPlayback: '/camstream/',
    sServerPreview: '?cmd=fetch',
    allAuthServers: [],
    usersOnline: null,
    machineStatus: null,
    dataLodaed: false,
    fEview: false,
    fController: false, // True if controller with member servers (Jump System capability)
    // Enterprise viewer (eview) state
    singleNvrMode: true,
    controller: {
      bSerial: 0,
      sName: '',
      sIP: '',
      fOnline: true
    },
    activeServerSerial: 0,
    servers: {},
    camerasByServer: {},
    // eView aggregated cameras from all connected servers
    // Each camera has: bID, bSerial, sName, sServerName, fEnable, compositeKey, serverUrl
    allCameras: [],
    // Per-server session tokens for eView multi-server streaming
    serverSessions: {}  // { bSerial: { sSess, serverUrl } }
  };
  
  export default function serverReducer(state = INITIAL_STATE, action) {
    switch ( action.type ) {
      case LOGOUT_USER:
        // Reset server state on logout - will be repopulated by getServer on next login
        // sName/bSerial reset so login screen shows correct server after jump/logout
        return {
          ...state,
          dataLodaed: false,
          bSerial: 0,
          bNumCams: 0,
          sName: '',
          sVersion: '',
          sVersionMajor: '',
          sReleaseVersion: '',
          fReleaseCandidate: false,
          sIP: '',
          bPort: 0,
          sApiToken: '',
          sLocalIP: '',
          fLocal: false,
          bTimestamp: 0,
          bTimeDiffmax: 36000,
          bAutoScanTimeout: 5,
          ptzConfig: [],
          ptzPresets: [],
          cameras: [],
          cameraArr: [],
          twoView: [],
          twoViewEnabledArr: [],
          quadView: [],
          quadViewEnabledArr: [],
          sixView: [],
          sixViewEnabledArr: [],
          eightView: [],
          eightViewEnabledArr: [],
          nineView: [],
          nineViewEnabledArr: [],
          twelveView: [],
          twelveViewEnabledArr: [],
          sixteenView: [],
          sixteenViewEnabledArr: [],
          singleViewEnabledArray: [],
          authServers: [],
          fController: false,
          activeServerSerial: 0,
          sServer: '',
          sServerLiveMPE: '/mpe/',
          sServerPlayback: '/camstream/',
          sServerPreview: '?cmd=fetch&session=1234&file=test.jpg',
          // Clear eView-specific data
          allCameras: [],
          camerasByServer: {},
          serverSessions: {}
        }
      case EXPIRE_SESSION:
        // Reset server state on session expiry
        return {
          ...state,
          dataLodaed: false,
          bSerial: 0,
          bNumCams: 0,
          sName: '',
          sVersion: '',
          sVersionMajor: '',
          sReleaseVersion: '',
          fReleaseCandidate: false,
          sIP: '',
          bPort: 0,
          sLocalIP: '',
          fLocal: false,
          bTimestamp: 0,
          bTimeDiffmax: 36000,
          bAutoScanTimeout: 5,
          ptzConfig: [],
          ptzPresets: [],
          cameras: [],
          cameraArr: [],
          twoView: [],
          twoViewEnabledArr: [],
          quadView: [],
          quadViewEnabledArr: [],
          sixView: [],
          sixViewEnabledArr: [],
          eightView: [],
          eightViewEnabledArr: [],
          nineView: [],
          nineViewEnabledArr: [],
          twelveView: [],
          twelveViewEnabledArr: [],
          sixteenView: [],
          sixteenViewEnabledArr: [],
          singleViewEnabledArray: [],
          authServers: [],
          fController: false,
          activeServerSerial: 0,
          sServer: '',
          sServerLiveMPE: '/mpe/',
          sServerPlayback: '/camstream/',
          sServerPreview: '?cmd=fetch&session=1234&file=test.jpg',
          // Clear eView-specific data
          allCameras: [],
          camerasByServer: {},
          serverSessions: {}
        }
      case GET_SERVER:
        return { 
          ...state, 
          bSerial: action.bSerial,
          sName: action.sName,
          sVersion: action.sVersion,
          sVersionMajor: (action.sVersion || '').split('.')[0],
          // ADR-183: what users see; sVersion stays the compatibility version.
          sReleaseVersion: action.sReleaseVersion || '',
          fReleaseCandidate: !!action.fReleaseCandidate,
          bNumCams: action.bNumcams,
          sApiToken: action.sApiToken,
          allAuthServers: action.authServers,
          usersOnline: action.usersOnline,
          fEview: action.fEview
        }
      // A tab left open across an NVR upgrade (refreshServerVersion): the version
      // fields only, so nothing else on the screen re-renders.
      case SET_SERVER_VERSION:
        return {
          ...state,
          sVersion: action.sVersion,
          sVersionMajor: (action.sVersion || '').split('.')[0],
          sReleaseVersion: action.sReleaseVersion || '',
          fReleaseCandidate: !!action.fReleaseCandidate
        }
      // CLOUD (#86bb5dx62): set ONLY the system name (the "Customer — Project" account
      // label) — cloud has no GET_SERVER config fetch, and we must not disturb the other
      // server fields (which stay at their cloud defaults so the ⓘ gates them).
      case SET_SYSTEM_NAME:
        return { ...state, sName: action.payload };
      case SET_AUTOSCAN_TIMEOUT:
        return {
          ...state,
          bAutoScanTimeout: action.payload
        }
      case SET_SYSTEM_OVERLAY:
        // Values come on every dispatch (login + optimistic toggle); the *Supported flags only on the
        // login capture — merge them conditionally so a toggle doesn't wipe support back to false.
        return {
          ...state,
          fLiveOverlay: !!action.payload.fLiveOverlay,
          fOverlayBackground: !!action.payload.fOverlayBackground,
          ...(action.payload.fLiveOverlaySupported !== undefined
            ? { fLiveOverlaySupported: !!action.payload.fLiveOverlaySupported } : {}),
          ...(action.payload.fOverlayBackgroundSupported !== undefined
            ? { fOverlayBackgroundSupported: !!action.payload.fOverlayBackgroundSupported } : {})
        }
      case SET_PTZ_CONFIG:
        return { 
          ...state, 
          ptzConfig: action.payload
        }
      case SET_PTZ_PRESETS:
        return { 
          ...state, 
          ptzPresets: action.payload
        }
      case SET_ALL_CAMERAS:
        let cameraArr = [];
        let singleViewEnabledArr = [];
        let quadView = [ ["1-4", true] ];
        let quadViewEnabledArr = [ "1-4" ];
        let sixView = [ ["1-6", true] ];
        let sixViewEnabledArr = [ "1-6" ];
        let nineView = [ ["1-9", true] ];
        let nineViewEnabledArr = [ "1-9" ];
        let twelveView = [ ["1-12", true] ];
        let twelveViewEnabledArr = [ "1-12" ];
        let sixteenView = [ ["1-16", true] ];
        let sixteenViewEnabledArr = [ "1-16" ];

        // Build cameraArr based on slot positions, not camera IDs
        for( let i = 0; i < action.payload.length; i++) {
            const cam = action.payload[i];
            const slotNumber = i + 1; // Slot numbers are 1-indexed for display
            const camEnabled = cam && cam.fEnable === true;
            if(camEnabled){
              // this is an array of slot numbers that are enabled
               singleViewEnabledArr.push(slotNumber);
            };
            // Format: [view_string, display_number, enabled]
            cameraArr.push([`cam_${slotNumber}`, slotNumber, camEnabled]);
        };

      // > 4 Cams
        if(action.payload.length > 4) {
          // check that at least one cam in this group is enabled - used for as
          if( singleViewEnabledArr.indexOf(5) < 0 && singleViewEnabledArr.indexOf(6) < 0 && 
              singleViewEnabledArr.indexOf(7) < 0 && singleViewEnabledArr.indexOf(8) < 0) {
            quadView.push( ["5-8", false] );
          } else {
            quadView.push( ["5-8", true] );
            quadViewEnabledArr.push( "5-8" );
          };
        };

      // > 6 Cams
        if(action.payload.length > 6) {
          if( singleViewEnabledArr.indexOf(7) < 0 && singleViewEnabledArr.indexOf(8) < 0 && 
              singleViewEnabledArr.indexOf(9) < 0 && singleViewEnabledArr.indexOf(10) < 0 &&
              singleViewEnabledArr.indexOf(11) < 0 && singleViewEnabledArr.indexOf(12) < 0) {
            sixView.push( ["7-12", false] );
          } else {
            sixView.push( ["7-12", true] );
            sixViewEnabledArr.push( "7-12" );
          };
        };

      // > 8 Cams
        if(action.payload.length > 8) {
          if( singleViewEnabledArr.indexOf(9) < 0 && singleViewEnabledArr.indexOf(10) < 0 && 
              singleViewEnabledArr.indexOf(11) < 0 && singleViewEnabledArr.indexOf(12) < 0) {
            quadView.push( ["9-12", false] );
          } else {
            quadView.push( ["9-12", true] );
            quadViewEnabledArr.push( "9-12" );
          };
        };

      // > 9 Cams
        if(action.payload.length > 9) {
          if( singleViewEnabledArr.indexOf(8) < 0 &&
              singleViewEnabledArr.indexOf(9) < 0 && singleViewEnabledArr.indexOf(10) < 0 && 
              singleViewEnabledArr.indexOf(11) < 0 && singleViewEnabledArr.indexOf(12) < 0 &&
              singleViewEnabledArr.indexOf(13) < 0 && singleViewEnabledArr.indexOf(14) < 0 && 
              singleViewEnabledArr.indexOf(15) < 0 && singleViewEnabledArr.indexOf(16) < 0) {
            nineView.push( ["8-16", false] );
          } else {
            nineView.push( ["8-16", true] );
            nineViewEnabledArr.push( "8-16" );
          };
        };

      // > 12 Cams
        if(action.payload.length > 12) {
          if( singleViewEnabledArr.indexOf(13) < 0 && singleViewEnabledArr.indexOf(14) < 0 && 
              singleViewEnabledArr.indexOf(15) < 0 && singleViewEnabledArr.indexOf(16) < 0) {
            quadView.push( ["13-16", false] );
          } else {
            quadView.push( ["13-16", true] );
            quadViewEnabledArr.push( "13-16" );
          };

          if( singleViewEnabledArr.indexOf(13) < 0 && singleViewEnabledArr.indexOf(14) < 0 && 
              singleViewEnabledArr.indexOf(15) < 0 && singleViewEnabledArr.indexOf(16) < 0 &&
              singleViewEnabledArr.indexOf(17) < 0 && singleViewEnabledArr.indexOf(18) < 0) {
            sixView.push( ["13-18", false] );
          } else {
            sixView.push( ["13-18", true] );
            sixViewEnabledArr.push( "13-18" );
          };

          if( singleViewEnabledArr.indexOf(13) < 0 && singleViewEnabledArr.indexOf(14) < 0 &&
              singleViewEnabledArr.indexOf(15) < 0 && singleViewEnabledArr.indexOf(16) < 0 && 
              singleViewEnabledArr.indexOf(17) < 0 && singleViewEnabledArr.indexOf(18) < 0 &&
              singleViewEnabledArr.indexOf(19) < 0 && singleViewEnabledArr.indexOf(20) < 0 && 
              singleViewEnabledArr.indexOf(21) < 0 && singleViewEnabledArr.indexOf(22) < 0 &&
              singleViewEnabledArr.indexOf(23) < 0 && singleViewEnabledArr.indexOf(24) < 0) {
            twelveView.push( ["13-24", false] );
          } else {
            twelveView.push( ["13-24", true] );
            twelveViewEnabledArr.push( "13-24" );
          };
        };

      // > 16 Cams
        if(action.payload.length > 16) {
          if( singleViewEnabledArr.indexOf(17) < 0 && singleViewEnabledArr.indexOf(18) < 0 && 
              singleViewEnabledArr.indexOf(19) < 0 && singleViewEnabledArr.indexOf(20) < 0) {
            quadView.push( ["17-20", false] );
          } else {
            quadView.push( ["17-20", true] );
            quadViewEnabledArr.push( "17-20" );
          };

          if( singleViewEnabledArr.indexOf(16) < 0 &&
              singleViewEnabledArr.indexOf(17) < 0 && singleViewEnabledArr.indexOf(18) < 0 && 
              singleViewEnabledArr.indexOf(19) < 0 && singleViewEnabledArr.indexOf(20) < 0 &&
              singleViewEnabledArr.indexOf(21) < 0 && singleViewEnabledArr.indexOf(22) < 0 && 
              singleViewEnabledArr.indexOf(23) < 0 && singleViewEnabledArr.indexOf(24) < 0) {
            nineView.push( ["16-24", false] );
          } else {
            nineView.push( ["16-24", true] );
            nineViewEnabledArr.push( "16-24" );
          };

          if( singleViewEnabledArr.indexOf(17) < 0 && singleViewEnabledArr.indexOf(18) < 0 &&
              singleViewEnabledArr.indexOf(19) < 0 && singleViewEnabledArr.indexOf(20) < 0 && 
              singleViewEnabledArr.indexOf(21) < 0 && singleViewEnabledArr.indexOf(22) < 0 &&
              singleViewEnabledArr.indexOf(23) < 0 && singleViewEnabledArr.indexOf(24) < 0 && 
              singleViewEnabledArr.indexOf(25) < 0 && singleViewEnabledArr.indexOf(26) < 0 &&
              singleViewEnabledArr.indexOf(27) < 0 && singleViewEnabledArr.indexOf(28) < 0 &&
              singleViewEnabledArr.indexOf(29) < 0 && singleViewEnabledArr.indexOf(30) < 0 &&
              singleViewEnabledArr.indexOf(31) < 0 && singleViewEnabledArr.indexOf(32) < 0 ) {
            sixteenView.push( ["17-32", false] );
          } else {
            sixteenView.push( ["17-32", true] );
            sixteenViewEnabledArr.push( "17-32" );
          };
        };

      // > 18 cams six view 18-24
        if(action.payload.length > 12) {
          if( singleViewEnabledArr.indexOf(19) < 0 && singleViewEnabledArr.indexOf(20) < 0 && 
              singleViewEnabledArr.indexOf(21) < 0 && singleViewEnabledArr.indexOf(22) < 0 &&
              singleViewEnabledArr.indexOf(23) < 0 && singleViewEnabledArr.indexOf(24) < 0) {
            sixView.push( ["19-24", false] );
          } else {
            sixView.push( ["19-24", true] );
            sixViewEnabledArr.push( "19-24" );
          };
        };


      // > 20 Cams
        if(action.payload.length > 20) {
          if( singleViewEnabledArr.indexOf(21) < 0 && singleViewEnabledArr.indexOf(22) < 0 && 
              singleViewEnabledArr.indexOf(23) < 0 && singleViewEnabledArr.indexOf(24) < 0) {
            quadView.push( ["21-24", false] );
          } else {
            quadView.push( ["21-24", true] );
            quadViewEnabledArr.push( "21-24" );
          };
        };

      // > 24 Cams
        if(action.payload.length > 24) {
          if( singleViewEnabledArr.indexOf(21) < 0 && singleViewEnabledArr.indexOf(22) < 0 && 
              singleViewEnabledArr.indexOf(23) < 0 && singleViewEnabledArr.indexOf(24) < 0) {
            quadView.push( ["25-28", false] );
          } else {
            quadView.push( ["25-28", true] );
            quadViewEnabledArr.push( "25-28" );
          };

          if( singleViewEnabledArr.indexOf(25) < 0 && singleViewEnabledArr.indexOf(26) < 0 && 
            singleViewEnabledArr.indexOf(27) < 0 && singleViewEnabledArr.indexOf(28) < 0 &&
            singleViewEnabledArr.indexOf(29) < 0 && singleViewEnabledArr.indexOf(30) < 0) {
            sixView.push( ["25-30", false] );
          } else {
            sixView.push( ["25-30", true] );
            sixViewEnabledArr.push( "25-30" );
          };

          if( singleViewEnabledArr.indexOf(24) < 0 &&
              singleViewEnabledArr.indexOf(25) < 0 && singleViewEnabledArr.indexOf(26) < 0 && 
              singleViewEnabledArr.indexOf(27) < 0 && singleViewEnabledArr.indexOf(28) < 0 &&
              singleViewEnabledArr.indexOf(29) < 0 && singleViewEnabledArr.indexOf(30) < 0 && 
              singleViewEnabledArr.indexOf(31) < 0 && singleViewEnabledArr.indexOf(32) < 0) {
            nineView.push( ["24-32", false] );
          } else {
            nineView.push( ["24-32", true] );
            nineViewEnabledArr.push( "24-32" );
          };
          
          if( singleViewEnabledArr.indexOf(25) < 0 && singleViewEnabledArr.indexOf(26) < 0 &&
              singleViewEnabledArr.indexOf(27) < 0 && singleViewEnabledArr.indexOf(28) < 0 && 
              singleViewEnabledArr.indexOf(29) < 0 && singleViewEnabledArr.indexOf(30) < 0 &&
              singleViewEnabledArr.indexOf(31) < 0 && singleViewEnabledArr.indexOf(32) < 0 && 
              singleViewEnabledArr.indexOf(33) < 0 && singleViewEnabledArr.indexOf(34) < 0 &&
              singleViewEnabledArr.indexOf(35) < 0 && singleViewEnabledArr.indexOf(36) < 0) {
            twelveView.push( ["25-36", false] );
          } else {
            twelveView.push( ["25-36", true] );
            twelveViewEnabledArr.push( "25-36" );
          };
        };

      // > 30 cams
        if(action.payload.length > 30) {
          if( singleViewEnabledArr.indexOf(31) < 0 && singleViewEnabledArr.indexOf(32) < 0 && 
              singleViewEnabledArr.indexOf(33) < 0 && singleViewEnabledArr.indexOf(34) < 0 &&
              singleViewEnabledArr.indexOf(35) < 0 && singleViewEnabledArr.indexOf(36) < 0) {
            sixView.push( ["31-36", false] );
          } else {
            sixView.push( ["31-36", true] );
            sixViewEnabledArr.push( "31-36" );
          };
        };

      // > 32 cams
        if(action.payload.length > 32) {
          if( singleViewEnabledArr.indexOf(33) < 0 && singleViewEnabledArr.indexOf(34) < 0 && 
              singleViewEnabledArr.indexOf(35) < 0 && singleViewEnabledArr.indexOf(36) < 0) {
            quadView.push( ["33-36", false] );
          } else {
            quadView.push( ["33-36", true] );
            quadViewEnabledArr.push( "33-36" );
          };
        
          if( singleViewEnabledArr.indexOf(32) < 0 &&
              singleViewEnabledArr.indexOf(33) < 0 && singleViewEnabledArr.indexOf(34) < 0 && 
              singleViewEnabledArr.indexOf(35) < 0 && singleViewEnabledArr.indexOf(36) < 0 &&
              singleViewEnabledArr.indexOf(37) < 0 && singleViewEnabledArr.indexOf(38) < 0 && 
              singleViewEnabledArr.indexOf(39) < 0 && singleViewEnabledArr.indexOf(40) < 0) {
            nineView.push( ["32-40", false] );
          } else {
            nineView.push( ["32-40", true] );
            nineViewEnabledArr.push( "32-40" );
          };
      
          if( singleViewEnabledArr.indexOf(33) < 0 && singleViewEnabledArr.indexOf(34) < 0 &&
              singleViewEnabledArr.indexOf(35) < 0 && singleViewEnabledArr.indexOf(36) < 0 && 
              singleViewEnabledArr.indexOf(37) < 0 && singleViewEnabledArr.indexOf(38) < 0 &&
              singleViewEnabledArr.indexOf(39) < 0 && singleViewEnabledArr.indexOf(40) < 0 && 
              singleViewEnabledArr.indexOf(41) < 0 && singleViewEnabledArr.indexOf(42) < 0 &&
              singleViewEnabledArr.indexOf(43) < 0 && singleViewEnabledArr.indexOf(44) < 0 &&
              singleViewEnabledArr.indexOf(45) < 0 && singleViewEnabledArr.indexOf(46) < 0 &&
              singleViewEnabledArr.indexOf(47) < 0 && singleViewEnabledArr.indexOf(48) < 0 ) {
            sixteenView.push( ["33-48", false] );
          } else {
            sixteenView.push( ["33-48", true] );
            sixteenViewEnabledArr.push( "33-48" );
          };
        };

      // > 36 cams
        if(action.payload.length > 36) {
          if( singleViewEnabledArr.indexOf(37) < 0 && singleViewEnabledArr.indexOf(38) < 0 && 
              singleViewEnabledArr.indexOf(39) < 0 && singleViewEnabledArr.indexOf(40) < 0) {
            quadView.push( ["37-40", false] );
          } else {
            quadView.push( ["37-40", true] );
            quadViewEnabledArr.push( "37-40" );
          };
    
          if( singleViewEnabledArr.indexOf(37) < 0 && singleViewEnabledArr.indexOf(38) < 0 && 
              singleViewEnabledArr.indexOf(39) < 0 && singleViewEnabledArr.indexOf(40) < 0 &&
              singleViewEnabledArr.indexOf(41) < 0 && singleViewEnabledArr.indexOf(42) < 0) {
            sixView.push( ["37-42", false] );
          } else {
            sixView.push( ["37-42", true] );
            sixViewEnabledArr.push( "37-42" );
          };
      
          if( singleViewEnabledArr.indexOf(37) < 0 && singleViewEnabledArr.indexOf(38) < 0 &&
              singleViewEnabledArr.indexOf(39) < 0 && singleViewEnabledArr.indexOf(40) < 0 && 
              singleViewEnabledArr.indexOf(41) < 0 && singleViewEnabledArr.indexOf(42) < 0 &&
              singleViewEnabledArr.indexOf(43) < 0 && singleViewEnabledArr.indexOf(44) < 0 && 
              singleViewEnabledArr.indexOf(45) < 0 && singleViewEnabledArr.indexOf(46) < 0 &&
              singleViewEnabledArr.indexOf(47) < 0 && singleViewEnabledArr.indexOf(48) < 0) {
            twelveView.push( ["37-48", false] );
          } else {
            twelveView.push( ["37-48", true] );
            twelveViewEnabledArr.push( "37-48" );
          };
        };

      // > 40 cams
        if(action.payload.length > 40) {
          if( singleViewEnabledArr.indexOf(41) < 0 && singleViewEnabledArr.indexOf(42) < 0 && 
              singleViewEnabledArr.indexOf(43) < 0 && singleViewEnabledArr.indexOf(44) < 0) {
            quadView.push( ["41-44", false] );
          } else {
            quadView.push( ["41-44", true] );
            quadViewEnabledArr.push( "41-44" );
          };
        
          if( singleViewEnabledArr.indexOf(40) < 0 &&
              singleViewEnabledArr.indexOf(41) < 0 && singleViewEnabledArr.indexOf(42) < 0 && 
              singleViewEnabledArr.indexOf(43) < 0 && singleViewEnabledArr.indexOf(44) < 0 &&
              singleViewEnabledArr.indexOf(45) < 0 && singleViewEnabledArr.indexOf(46) < 0 && 
              singleViewEnabledArr.indexOf(47) < 0 && singleViewEnabledArr.indexOf(48) < 0) {
            nineView.push( ["40-48", false] );
          } else {
            nineView.push( ["40-48", true] );
            nineViewEnabledArr.push( "40-48" );
          };
        };

      // > 42 cams
        if(action.payload.length > 42) {
          if( singleViewEnabledArr.indexOf(43) < 0 && singleViewEnabledArr.indexOf(44) < 0 &&
              singleViewEnabledArr.indexOf(45) < 0 && singleViewEnabledArr.indexOf(46) < 0 &&
              singleViewEnabledArr.indexOf(47) < 0 && singleViewEnabledArr.indexOf(48) < 0) {
            sixView.push( ["43-48", false] );
          } else {
            sixView.push( ["43-48", true] );
            sixViewEnabledArr.push( "43-48" );
          };
        };

      // > 44 cams
        if(action.payload.length > 44) {
          if( singleViewEnabledArr.indexOf(45) < 0 && singleViewEnabledArr.indexOf(46) < 0 && 
              singleViewEnabledArr.indexOf(47) < 0 && singleViewEnabledArr.indexOf(48) < 0) {
            quadView.push( ["45-48", false] );
          } else {
            quadView.push( ["45-48", true] );
            quadViewEnabledArr.push( "45-48" );
          };
        };

      // > 48 cams
        if(action.payload.length > 48) {
          if( singleViewEnabledArr.indexOf(49) < 0 && singleViewEnabledArr.indexOf(50) < 0 && 
              singleViewEnabledArr.indexOf(51) < 0 && singleViewEnabledArr.indexOf(52) < 0) {
            quadView.push( ["49-52", false] );
          } else {
            quadView.push( ["49-52", true] );
            quadViewEnabledArr.push( "49-52" );
          };
        
          if( singleViewEnabledArr.indexOf(49) < 0 && singleViewEnabledArr.indexOf(50) < 0 && 
              singleViewEnabledArr.indexOf(51) < 0 && singleViewEnabledArr.indexOf(52) < 0 &&
              singleViewEnabledArr.indexOf(53) < 0 && singleViewEnabledArr.indexOf(54) < 0) {
            sixView.push( ["49-54", false] );
          } else {
            sixView.push( ["49-54", true] );
            sixViewEnabledArr.push( "49-54" );
          };
          
          if( singleViewEnabledArr.indexOf(48) < 0 &&
              singleViewEnabledArr.indexOf(49) < 0 && singleViewEnabledArr.indexOf(50) < 0 && 
              singleViewEnabledArr.indexOf(51) < 0 && singleViewEnabledArr.indexOf(52) < 0 &&
              singleViewEnabledArr.indexOf(53) < 0 && singleViewEnabledArr.indexOf(54) < 0 && 
              singleViewEnabledArr.indexOf(55) < 0 && singleViewEnabledArr.indexOf(56) < 0) {
            nineView.push( ["48-56", false] );
          } else {
            nineView.push( ["48-56", true] );
            nineViewEnabledArr.push( "48-56" );
          };
      
          if( singleViewEnabledArr.indexOf(49) < 0 && singleViewEnabledArr.indexOf(50) < 0 &&
              singleViewEnabledArr.indexOf(51) < 0 && singleViewEnabledArr.indexOf(52) < 0 && 
              singleViewEnabledArr.indexOf(53) < 0 && singleViewEnabledArr.indexOf(54) < 0 &&
              singleViewEnabledArr.indexOf(55) < 0 && singleViewEnabledArr.indexOf(56) < 0 && 
              singleViewEnabledArr.indexOf(57) < 0 && singleViewEnabledArr.indexOf(58) < 0 &&
              singleViewEnabledArr.indexOf(59) < 0 && singleViewEnabledArr.indexOf(60) < 0) {
            twelveView.push( ["49-60", false] );
          } else {
            twelveView.push( ["49-60", true] );
            twelveViewEnabledArr.push( "49-60" );
          };
  
          if( singleViewEnabledArr.indexOf(49) < 0 && singleViewEnabledArr.indexOf(50) < 0 &&
              singleViewEnabledArr.indexOf(51) < 0 && singleViewEnabledArr.indexOf(52) < 0 && 
              singleViewEnabledArr.indexOf(53) < 0 && singleViewEnabledArr.indexOf(54) < 0 &&
              singleViewEnabledArr.indexOf(55) < 0 && singleViewEnabledArr.indexOf(56) < 0 && 
              singleViewEnabledArr.indexOf(57) < 0 && singleViewEnabledArr.indexOf(58) < 0 &&
              singleViewEnabledArr.indexOf(59) < 0 && singleViewEnabledArr.indexOf(60) < 0 &&
              singleViewEnabledArr.indexOf(61) < 0 && singleViewEnabledArr.indexOf(62) < 0 &&
              singleViewEnabledArr.indexOf(63) < 0 && singleViewEnabledArr.indexOf(64) < 0 ) {
            sixteenView.push( ["49-64", false] );
          } else {
            sixteenView.push( ["49-64", true] );
            sixteenViewEnabledArr.push( "49-64" );
          };
        };

      // > 52 cams
        if(action.payload.length > 52) {
          if( singleViewEnabledArr.indexOf(53) < 0 && singleViewEnabledArr.indexOf(54) < 0 && 
              singleViewEnabledArr.indexOf(55) < 0 && singleViewEnabledArr.indexOf(56) < 0) {
            quadView.push( ["53-56", false] );
          } else {
            quadView.push( ["53-56", true] );
            quadViewEnabledArr.push( "53-56" );
          };
        };

      // > 54 cams
        if(action.payload.length > 54) {
          if( singleViewEnabledArr.indexOf(55) < 0 && singleViewEnabledArr.indexOf(56) < 0 && 
              singleViewEnabledArr.indexOf(57) < 0 && singleViewEnabledArr.indexOf(58) < 0 &&
              singleViewEnabledArr.indexOf(59) < 0 && singleViewEnabledArr.indexOf(60) < 0) {
            sixView.push( ["55-60", false] );
          } else {
            sixView.push( ["55-60", true] );
            sixViewEnabledArr.push( "55-60" );
          };
        };

      // > 56 cams
        if(action.payload.length > 56) {
          if( singleViewEnabledArr.indexOf(57) < 0 && singleViewEnabledArr.indexOf(58) < 0 && 
              singleViewEnabledArr.indexOf(59) < 0 && singleViewEnabledArr.indexOf(60) < 0) {
            quadView.push( ["57-60", false] );
          } else {
            quadView.push( ["57-60", true] );
            quadViewEnabledArr.push( "57-60" );
          };
        
          if( singleViewEnabledArr.indexOf(56) < 0 &&
              singleViewEnabledArr.indexOf(57) < 0 && singleViewEnabledArr.indexOf(58) < 0 && 
              singleViewEnabledArr.indexOf(59) < 0 && singleViewEnabledArr.indexOf(60) < 0 &&
              singleViewEnabledArr.indexOf(61) < 0 && singleViewEnabledArr.indexOf(62) < 0 && 
              singleViewEnabledArr.indexOf(63) < 0 && singleViewEnabledArr.indexOf(64) < 0) {
            nineView.push( ["56-64", false] );
          } else {
            nineView.push( ["56-64", true] );
            nineViewEnabledArr.push( "56-64" );
          };
        };

      // > 60 cams
        if(action.payload.length > 60) {
          if( singleViewEnabledArr.indexOf(61) < 0 && singleViewEnabledArr.indexOf(62) < 0 && 
              singleViewEnabledArr.indexOf(63) < 0 && singleViewEnabledArr.indexOf(64) < 0) {
            quadView.push( ["61-64", false] );
          } else {
            quadView.push( ["61-64", true] );
            quadViewEnabledArr.push( "61-64" );
          };
          
          if( singleViewEnabledArr.indexOf(61) < 0 && singleViewEnabledArr.indexOf(62) < 0 && 
              singleViewEnabledArr.indexOf(63) < 0 && singleViewEnabledArr.indexOf(64) < 0 &&
              singleViewEnabledArr.indexOf(65) < 0 && singleViewEnabledArr.indexOf(66) < 0) {
            sixView.push( ["61-66", false] );
          } else {
            sixView.push( ["61-66", true] );
            sixViewEnabledArr.push( "61-66" );
          };
          
          if( singleViewEnabledArr.indexOf(61) < 0 && singleViewEnabledArr.indexOf(62) < 0 &&
              singleViewEnabledArr.indexOf(63) < 0 && singleViewEnabledArr.indexOf(64) < 0 && 
              singleViewEnabledArr.indexOf(65) < 0 && singleViewEnabledArr.indexOf(66) < 0 &&
              singleViewEnabledArr.indexOf(67) < 0 && singleViewEnabledArr.indexOf(68) < 0 && 
              singleViewEnabledArr.indexOf(69) < 0 && singleViewEnabledArr.indexOf(70) < 0 &&
              singleViewEnabledArr.indexOf(71) < 0 && singleViewEnabledArr.indexOf(72) < 0) {
            twelveView.push( ["61-72", false] );
          } else {
            twelveView.push( ["61-72", true] );
            twelveViewEnabledArr.push( "61-72" );
          };
        };

        // conf-2 (pages of 2) + conf-8 (pages of 8) bank ranges — #86bb5dx62 L-A1
        const { view: twoView, enabledArr: twoViewEnabledArr } = buildPagedRanges(2, action.payload.length, singleViewEnabledArr);
        const { view: eightView, enabledArr: eightViewEnabledArr } = buildPagedRanges(8, action.payload.length, singleViewEnabledArr);

        return {
          ...state,
          cameras: action.payload,
          cameraArr,
          singleViewEnabledArr,
          twoView,
          twoViewEnabledArr,
          quadView,
          quadViewEnabledArr,
          sixView,
          sixViewEnabledArr,
          eightView,
          eightViewEnabledArr,
          nineView,
          nineViewEnabledArr,
          twelveView,
          twelveViewEnabledArr,
          sixteenView,
          sixteenViewEnabledArr
        }
      case SET_AUTH_SERVERS:
        // Only store auth servers list - bSerial comes from GET_SERVER (getServerConf)
        return {
          ...state,
          authServers: action.payload,
          dataLoaded: true,
          sApiToken: action.sApiToken,
          cloudAPI: 'https://api.cloud.dividia.net/uploads'
        }
      case SET_EVIEW_MODE:
        return {
          ...state,
          fEview: action.payload,
          singleNvrMode: !action.payload
        }
      case SET_CONTROLLER_MODE:
        return {
          ...state,
          fController: action.payload
        }
      case SET_CONTROLLER_INFO:
        return {
          ...state,
          controller: {
            bSerial: action.payload.bSerial,
            sName: action.payload.sName,
            sIP: action.payload.sIP,
            fOnline: action.payload.fOnline !== undefined ? action.payload.fOnline : true
          }
        }
      case SET_ALL_SERVERS:
        const serversById = {};
        action.payload.forEach(server => {
          serversById[server.bSerial] = {
            ...server,
            fOnline: server.fOnline !== undefined ? server.fOnline : true
          };
        });
        return {
          ...state,
          servers: serversById
        }
      case SET_ACTIVE_SERVER: {
        // Filter cameras for new active server
        const activeCameras = state.cameras.filter(c => c.bSerial === action.payload);
        const viewArrays = buildCameraArrays(activeCameras);

        return {
          ...state,
          activeServerSerial: action.payload,
          ...viewArrays
        };
      }
      case SET_ENTERPRISE_CAMERAS: {
        const camerasWithKeys = action.payload.map(cam => ({
          ...cam,
          compositeKey: `${cam.bSerial}_${cam.bID}`
        }));

        const camerasByServer = {};
        camerasWithKeys.forEach(cam => {
          if (!camerasByServer[cam.bSerial]) {
            camerasByServer[cam.bSerial] = [];
          }
          camerasByServer[cam.bSerial].push(cam);
        });

        // Build camera arrays for active server (for CamButtonsView)
        const activeSerial = state.activeServerSerial || state.bSerial;
        const activeCameras = camerasWithKeys.filter(c => c.bSerial === activeSerial);
        const viewArrays = buildCameraArrays(activeCameras);

        return {
          ...state,
          cameras: camerasWithKeys,
          camerasByServer,
          ...viewArrays
        };
      }
      case SET_ALL_EVIEW_CAMERAS: {
        // Aggregate cameras from all connected servers for eView mode
        // Each camera has: bID, bSerial, sName, sServerName, fEnable, compositeKey, serverUrl
        const allCameras = action.payload.map(cam => ({
          ...cam,
          compositeKey: `nvr_${cam.bSerial}_cam_${cam.bID}`
        }));

        // Also build camerasByServer for quick lookup
        const camerasByServer = {};
        allCameras.forEach(cam => {
          if (!camerasByServer[cam.bSerial]) {
            camerasByServer[cam.bSerial] = [];
          }
          camerasByServer[cam.bSerial].push(cam);
        });

        debugLog('[ServerReducer] SET_ALL_EVIEW_CAMERAS:', allCameras.length, 'cameras from', Object.keys(camerasByServer).length, 'servers');

        return {
          ...state,
          allCameras,
          camerasByServer
        };
      }
      case SET_SERVER_SESSIONS: {
        // Store session tokens and URLs for each server (for multi-server streaming)
        return {
          ...state,
          serverSessions: {
            ...state.serverSessions,
            ...action.payload
          }
        };
      }
      case ASSIGN_CAMERA_TO_SLOT:
        return {
          ...state,
          cameras: action.payload
        };
      case JUMP_SYSTEM_START:
        // Clear the previous server's camera/view/status state at the START of a
        // jump so the live grid doesn't briefly render stale cells against the
        // new server's identity. Prior settle-time fixes (currentCamView realign,
        // cam-less fallback, conf desync) all addressed the post-jump state and
        // missed this transient window. See ClickUp 86baduzfv.
        //
        // Server identity (bSerial/sName/sIP/sLocalIP/bPort) is intentionally
        // NOT cleared here — GET_SERVER replaces it atomically during the jump
        // RPCs, and clearing it would NaN-out components that key on bSerial.
        return {
          ...state,
          cameras: [],
          cameraArr: [],
          singleViewEnabledArr: [],
          singleViewEnabledArray: [],
          twoView: [],
          twoViewEnabledArr: [],
          quadView: [],
          quadViewEnabledArr: [],
          sixView: [],
          sixViewEnabledArr: [],
          eightView: [],
          eightViewEnabledArr: [],
          nineView: [],
          nineViewEnabledArr: [],
          twelveView: [],
          twelveViewEnabledArr: [],
          sixteenView: [],
          sixteenViewEnabledArr: [],
          // Clear eview aggregates too — the new server's SET_ALL_EVIEW_CAMERAS
          // replaces these mid-jump, but until then the cross-NVR camera tree
          // in eView would still list the prior controller's child cameras.
          allCameras: [],
          camerasByServer: {},
          serverSessions: {},
          // Clear StatusView-backing fields so its empty-state placeholders
          // show during the jump instead of the prior server's values.
          usersOnline: null,
          machineStatus: null,
          sVersion: '',
          sVersionMajor: '',
          sReleaseVersion: '',
          fReleaseCandidate: false
        };
      case JUMP_SYSTEM:
        // Update server info when jumping to a new server
        // Mirrors Java dview behavior: after jump, display new server's info
        const jumpedServer = action.payload.server;
        return {
          ...state,
          bSerial: jumpedServer.bSerial,
          sName: jumpedServer.sName,
          sIP: jumpedServer.sIP,
          sLocalIP: jumpedServer.sLocalIP,
          bPort: jumpedServer.bPort,
          sVersion: jumpedServer.sVersion,
          sVersionMajor: jumpedServer.sVersion ? jumpedServer.sVersion.split('.')[0] : state.sVersionMajor,
          // Connected-server entries carry no release: show sVersion until the
          // jumped-to server's own config.general arrives.
          sReleaseVersion: jumpedServer.sReleaseVersion || '',
          fReleaseCandidate: !!jumpedServer.fReleaseCandidate
        };
      case SET_MACHINE_STATUS:
        return {
          ...state,
          machineStatus: action.payload
        };
      case SET_USERS_ONLINE:
        return {
          ...state,
          usersOnline: action.payload
        };
      default:
        return state;
    };
  };
